import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

export default function HomePage() {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    area: '',
    acreage: '',
    crop: '',
    schedule: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('/php/contact.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      let ok = response.ok;
      try {
        const data = (await response.json()) as { ok?: boolean };
        if (typeof data?.ok === 'boolean') ok = data.ok;
      } catch {
        // non-JSON response: fall back to HTTP status
      }

      if (ok) {
        setFormData({
          name: '',
          phone: '',
          email: '',
          area: '',
          acreage: '',
          crop: '',
          schedule: '',
          message: ''
        });
        navigate('/thanks');
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const scrollToForm = (e: React.MouseEvent) => {
    e.preventDefault();
    const formSection = document.getElementById('contact-form');
    if (formSection) {
      formSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white/95 backdrop-blur-sm shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center">
              <img src="https://static.readdy.ai/image/128f281c68d0bcc2a1e3f255cb4aab26/044fcb80aa421476fb1a240214d755ab.jpeg" alt="ドローン農薬散布サービス" className="h-12 w-auto" />
            </div>
            
            <div className="hidden md:flex items-center space-x-8">
              <a href="#merit" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap">サービスの特徴</a>
              <a href="#price" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap">料金プラン</a>
              <a href="#area" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap">対応エリア</a>
              <a href="#flow" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap">ご利用の流れ</a>
              <a href="#company" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap">会社概要</a>
              <a href="#contact-form" onClick={scrollToForm} className="px-6 py-2.5 bg-teal-600 text-white text-sm font-medium rounded-full hover:bg-teal-700 transition-colors whitespace-nowrap">無料見積もり</a>
            </div>

            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="md:hidden p-2">
              <i className={`${isMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl text-gray-700`}></i>
            </button>
          </div>
        </div>

        {isMenuOpen && (
          <div className="md:hidden bg-white border-t">
            <div className="px-4 py-4 space-y-3">
              <a href="#merit" onClick={() => setIsMenuOpen(false)} className="block text-sm font-medium text-gray-700 hover:text-teal-600 py-2">サービスの特徴</a>
              <a href="#price" onClick={() => setIsMenuOpen(false)} className="block text-sm font-medium text-gray-700 hover:text-teal-600 py-2">料金プラン</a>
              <a href="#area" onClick={() => setIsMenuOpen(false)} className="block text-sm font-medium text-gray-700 hover:text-teal-600 py-2">対応エリア</a>
              <a href="#flow" onClick={() => setIsMenuOpen(false)} className="block text-sm font-medium text-gray-700 hover:text-teal-600 py-2">ご利用の流れ</a>
              <a href="#company" onClick={() => setIsMenuOpen(false)} className="block text-sm font-medium text-gray-700 hover:text-teal-600 py-2">会社概要</a>
              <Link to="/commercial-transaction" onClick={() => setIsMenuOpen(false)} className="block text-sm font-medium text-gray-700 hover:text-teal-600 py-2">特定商取引法に関する表記</Link>
              <Link to="/privacy-policy" onClick={() => setIsMenuOpen(false)} className="block text-sm font-medium text-gray-700 hover:text-teal-600 py-2">プライバシーポリシー</Link>
              <a href="#contact-form" onClick={scrollToForm} className="block px-6 py-2.5 bg-teal-600 text-white text-sm font-medium rounded-full hover:bg-teal-700 transition-colors text-center">無料見積もり</a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center pt-20">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://readdy.ai/api/search-image?query=aerial%20view%20of%20vast%20green%20rice%20paddy%20fields%20in%20rural%20Japan%20Ibaraki%20prefecture%20with%20mountains%20in%20background%2C%20bright%20sunny%20day%2C%20clean%20simple%20agricultural%20landscape%2C%20peaceful%20countryside%20scenery%2C%20natural%20lighting%2C%20professional%20photography%2C%20high%20quality%2C%20serene%20atmosphere&width=1920&height=1080&seq=hero-bg-001&orientation=landscape" 
            alt="茨城県の田園風景" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center w-full">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-loose">
            茨城県全域対応<br />
            ドローン農薬散布代行<br />
            <span className="text-3xl sm:text-4xl lg:text-5xl mt-6 block">重労働の防除作業を、<br />最速１反約10分〜・1反2,000円から</span>
          </h1>

          <p className="text-2xl sm:text-3xl text-white mb-8 font-medium">
            3ヶ月前予約で1反1,900円（県内最安）。<br />
            高齢化・人手不足・条件不利地のほ場でも、暑さと重労働からあなたを解放します。
          </p>

          <p className="text-lg sm:text-xl text-white/95 mb-12 max-w-3xl mx-auto leading-relaxed space-y-2">
            「年々、農薬散布がつらくなってきた」<br />
            「人手が足りず、防除のタイミングが遅れがち」<br />
            そんな茨城県の稲作農家さん・農業法人・野菜・果樹農家さんのために、ドローンによる農薬散布代行サービスをご用意しました。<br />
            最速１反約10分〜の高速散布で、炎天下の重労働を代わりに引き受けます。
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a href="#contact-form" onClick={scrollToForm} className="px-8 py-4 bg-teal-600 text-white text-xl font-bold rounded-full hover:bg-teal-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap">
              まずは無料見積りを依頼する
            </a>
            <a href="tel:08022504380" className="px-8 py-4 bg-white text-teal-600 text-xl font-bold rounded-full hover:bg-gray-50 transition-all shadow-lg hover:shadow-xl whitespace-nowrap">
              電話で相談する（9時〜17時）
            </a>
          </div>

          <p className="text-base text-white/90 mt-8">
            対応エリア：鉾田・行方・小美玉・霞ヶ浦・茨城町・大洗・つくば市 ほか茨城県内全域
          </p>
        </div>
      </section>

      {/* 悩みと課題 Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              もう、その散布作業を続けなくてもいいかもしれません。
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              背負い動噴で1日かけて散布していた頃に比べて体力が落ちたと感じていませんか？<br />
              一度に広い面積を防除しなければならないのに<br />
              人手も時間も足りないと感じていませんか？
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-user-heart-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">高齢化による体力の限界</h3>
              <p className="text-lg text-gray-600 leading-relaxed">高齢のご夫婦だけで田んぼを守っていて、背負い散布が年々つらくなっている。</p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-sun-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">熱中症のリスク</h3>
              <p className="text-lg text-gray-600 leading-relaxed">暑い中、一日中ほ場を歩き回る防除作業で、熱中症が心配だ。</p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-cloud-windy-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">タイミングの難しさ</h3>
              <p className="text-lg text-gray-600 leading-relaxed">雨や風で散布タイミングがずれ、病害虫や雑草の発生がいつも不安。</p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-map-pin-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">条件不利地での困難</h3>
              <p className="text-lg text-gray-600 leading-relaxed">ブームスプレーヤーが入りづらい中山間地・変形地・ぬかるんだ田んぼの散布に困っている。</p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-funds-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">導入コストの壁</h3>
              <p className="text-lg text-gray-600 leading-relaxed">ドローンを自分で買おうか迷ったが、導入費用や資格・維持管理がネックで踏み切れない。</p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-team-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">人手不足</h3>
              <p className="text-lg text-gray-600 leading-relaxed">少人数で広い面積を管理しなければならず、防除作業に手が回らない。</p>
            </div>
          </div>

          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">
              そんな「毎年の当たり前になっていたつらい作業」こそ、<br />
              ドローン散布に外注する価値があります。
            </p>
          </div>
        </div>
      </section>

      {/* ドローン散布のメリット Section */}
      <section id="merit" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              最速1反10分〜の高速散布で、重労働と危険から解放
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              農薬散布用ドローンを使えば、1反（10a）あたり約10分〜で散布が完了するケースが一般的です。<br />
              手作業や背負い動噴と比べて、作業時間は約5分の1程度に短縮でき、作業者は圃場の外から安全に見守るだけで済みます。
            </p>
            <p className="text-sm text-gray-500 leading-relaxed mt-4 max-w-3xl mx-auto">
              ※基本的に、当日立ち会いいただきながら作業させていただきます。ご都合により、立ち会いいただくこと無く、作業することも可能です
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=modern%20agricultural%20drone%20spraying%20pesticides%20over%20green%20rice%20field%20in%20Japan%2C%20aerial%20view%2C%20high%20tech%20farming%2C%20bright%20daylight%2C%20clean%20simple%20background%2C%20professional%20photography%2C%20technology%20in%20agriculture&width=800&height=600&seq=drone-spray-001&orientation=landscape" 
                alt="ドローン散布作業" 
                className="w-full h-96 object-cover rounded-2xl shadow-lg"
              />
            </div>

            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center">
                  <i className="ri-time-line text-xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">時間短縮</h3>
                  <p className="text-lg text-gray-600 leading-relaxed">最速１反約10分〜で散布が完了。広い面積でも短時間で一斉防除が可能です。</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center">
                  <i className="ri-user-smile-line text-xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">省力化・省人化</h3>
                  <p className="text-lg text-gray-600 leading-relaxed">高齢の方や少人数の農家でも、広い面積を無理なく管理できます。</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center">
                  <i className="ri-shield-check-line text-xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">安全性</h3>
                  <p className="text-lg text-gray-600 leading-relaxed">作業者はドローンを遠隔操作するだけなので、ぬかるみや傾斜地に入る必要がありません。</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center">
                  <i className="ri-plant-line text-xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">農薬の適量散布</h3>
                  <p className="text-lg text-gray-600 leading-relaxed">圃場条件に合わせた均一散布ができ、スマート農業の取り組みの中では農薬使用量を抑えられた事例も報告されています。</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-teal-50 p-8 rounded-2xl text-center">
            <p className="text-2xl font-bold text-gray-900">
              「作業時間の短縮」「身体への負担軽減」「安全性の確保」<br />
              の三つを同時に改善できるのが、ドローン散布の大きな強みです。
            </p>
          </div>
        </div>
      </section>

      {/* 料金プラン Section */}
      <section id="price" className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              相場より導入しやすい価格。早割なら1反1,900円（県内最安）
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              一般的なドローン農薬散布代行の料金相場は、1反あたり2,000〜3,000円と言われています。<br />
              当サービスでは、茨城県内の農家さんにとって利用しやすい価格として、1反あたり2,000円の基本料金をご用意しました。
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-white p-8 rounded-2xl shadow-lg border-2 border-gray-200">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">基本料金</h3>
                <div className="text-5xl font-bold text-teal-600 mb-2">¥2,000</div>
                <p className="text-lg text-gray-600">1反（10a）あたり（税別）</p>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-xl text-teal-600 flex-shrink-0 mt-0.5"></i>
                  <span className="text-lg text-gray-700">ドローン操縦・散布作業</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-xl text-teal-600 flex-shrink-0 mt-0.5"></i>
                  <span className="text-lg text-gray-700">機材費を含みます。運搬料は別途。詳しくはお見積もりご依頼時にお伺いください</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-xl text-teal-600 flex-shrink-0 mt-0.5"></i>
                  <span className="text-lg text-gray-700">有資格オペレーター対応（当社はUTC有資格業者です）</span>
                </li>
              </ul>
            </div>

            <div className="flex flex-col">
              <div className="bg-gradient-to-br from-teal-600 to-teal-700 p-8 rounded-2xl shadow-lg relative overflow-hidden">
                <div className="absolute top-4 right-4 bg-yellow-400 text-gray-900 px-4 py-1 rounded-full text-base font-bold">
                  おすすめ
                </div>
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-white mb-2">早割プラン</h3>
                  <div className="text-5xl font-bold text-white mb-2">¥1,900</div>
                  <p className="text-lg text-teal-100">1反（10a）あたり（税別）</p>
                  <p className="text-base text-teal-100 mt-2">散布予定日の3ヶ月前までのご予約</p>
                </div>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-xl text-white flex-shrink-0 mt-0.5"></i>
                    <span className="text-lg text-white">基本料金の全サービス込み</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-xl text-white flex-shrink-0 mt-0.5"></i>
                    <span className="text-lg text-white">100円/反の割引</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-xl text-white flex-shrink-0 mt-0.5"></i>
                    <span className="text-lg text-white">計画的な防除スケジュール</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-xl text-white flex-shrink-0 mt-0.5"></i>
                    <span className="text-lg text-white font-bold">割引適用後、県内最安</span>
                  </li>
                </ul>
              </div>
              <p className="text-base text-teal-700 font-medium mt-3 text-center">
                ※シーズン中のリピーターの方にはさらに割引適応あり
              </p>
            </div>
          </div>

          <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-200">
            <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">価格の納得感</h3>
            <p className="text-lg text-gray-700 leading-relaxed text-center max-w-3xl mx-auto">
              タイミングさえ事前にお知らせいただければ、相場よりも安い単価で防除をお任せいただけます。<br />
              防除計画があらかじめ決まっている農家さんほど、早割の恩恵を受けやすい料金設計です。
            </p>
          </div>

          <div className="text-center mt-12">
            <p className="text-xl font-bold text-gray-900 mb-6">
              今年の防除スケジュールが分かる方は、早めのご相談がお得です。
            </p>
            <a href="#contact-form" onClick={scrollToForm} className="inline-block px-8 py-4 bg-teal-600 text-white text-xl font-bold rounded-full hover:bg-teal-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap">
              無料見積もりを依頼する
            </a>
          </div>
        </div>
      </section>

      {/* 対応作物・対象エリア Section */}
      <section id="area" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              水稲から芋、馬鈴薯・野菜・果樹まで。茨城県内全域が対象です
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              当サービスでは、水稲・芋・馬鈴薯はもちろん、麦・そば・野菜・果樹など、ほとんどの農作物の防除に対応しています。<br />
              茨城県は、米どころであると同時に、野菜や果樹、畑作も盛んな地域です。複数作物を栽培している農家さんでも、まとめてご相談いただけます。
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 mb-16">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <i className="ri-plant-line text-3xl text-teal-600"></i>
                対応作物例
              </h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3 bg-gray-50 p-4 rounded-xl">
                  <i className="ri-checkbox-circle-fill text-xl text-teal-600 flex-shrink-0 mt-0.5"></i>
                  <div>
                    <strong className="text-lg text-gray-900">水稲</strong>
                    <p className="text-base text-gray-600">本田での除草剤・病害虫防除など</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 bg-gray-50 p-4 rounded-xl">
                  <i className="ri-checkbox-circle-fill text-xl text-teal-600 flex-shrink-0 mt-0.5"></i>
                  <div>
                    <strong className="text-lg text-gray-900">芋・馬鈴薯</strong>
                    <p className="text-base text-gray-600">さつまいも・じゃがいもなどの病害虫防除</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 bg-gray-50 p-4 rounded-xl">
                  <i className="ri-checkbox-circle-fill text-xl text-teal-600 flex-shrink-0 mt-0.5"></i>
                  <div>
                    <strong className="text-lg text-gray-900">麦・そば</strong>
                    <p className="text-base text-gray-600">畑作物の防除</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 bg-gray-50 p-4 rounded-xl">
                  <i className="ri-checkbox-circle-fill text-xl text-teal-600 flex-shrink-0 mt-0.5"></i>
                  <div>
                    <strong className="text-lg text-gray-900">露地野菜</strong>
                    <p className="text-base text-gray-600">キャベツ・レタス・葉物など</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 bg-gray-50 p-4 rounded-xl">
                  <i className="ri-checkbox-circle-fill text-xl text-teal-600 flex-shrink-0 mt-0.5"></i>
                  <div>
                    <strong className="text-lg text-gray-900">果樹</strong>
                    <p className="text-base text-gray-600">柿・梨・その他果樹園の防除</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 bg-gray-50 p-4 rounded-xl">
                  <i className="ri-checkbox-circle-fill text-xl text-teal-600 flex-shrink-0 mt-0.5"></i>
                  <div>
                    <strong className="text-lg text-gray-900">その他</strong>
                    <p className="text-base text-gray-600">ご相談に応じて対応</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <i className="ri-map-pin-2-line text-3xl text-teal-600"></i>
                対象エリア
              </h3>
              <div className="bg-teal-50 p-6 rounded-2xl">
                <p className="text-base font-bold text-gray-900 mb-4">茨城県内全域対応</p>
                <div className="grid grid-cols-2 gap-2 text-base text-gray-700">
                  <div>鉾田市</div>
                  <div>つくば市</div>
                  <div>土浦市</div>
                  <div>水戸市</div>
                  <div>日立市</div>
                  <div>石岡市</div>
                  <div>古河市</div>
                  <div>結城市</div>
                  <div>龍ケ崎市</div>
                  <div>下妻市</div>
                  <div>常総市</div>
                  <div>高萩市</div>
                  <div>北茨城市</div>
                  <div>笠間市</div>
                  <div>取手市</div>
                  <div>牛久市</div>
                  <div>ひたちなか市</div>
                  <div>鹿嶋市</div>
                  <div>潮来市</div>
                  <div>守谷市</div>
                  <div>常陸大宮市</div>
                  <div>那珂市</div>
                  <div>筑西市</div>
                  <div>霞ヶ浦市</div>
                  <div>桜川市</div>
                  <div>神栖市</div>
                  <div>行方市</div>
                  <div>小美玉市</div>
                  <div>茨城町</div>
                  <div>大洗町</div>
                  <div>城里町</div>
                  <div>東海村</div>
                  <div>大子町</div>
                  <div>美浦村</div>
                  <div>五霞町</div>
                  <div>境町</div>
                </div>
                <p className="text-base text-gray-600 mt-4">
                  上記以外の市町村でも、茨城県内であればまずはお気軽にご相談ください。
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-teal-600 to-teal-700 p-8 rounded-2xl text-center text-white">
            <h3 className="text-2xl font-bold mb-4">こんな農家さん・農業法人のためのサービスです</h3>
            <div className="grid md:grid-cols-2 gap-4 text-left max-w-4xl mx-auto">
              <div className="flex items-start gap-3">
                <i className="ri-check-double-line text-xl flex-shrink-0 mt-0.5"></i>
                <span className="text-base">高齢のご夫婦だけで稲作を続けている農家さん</span>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-check-double-line text-xl flex-shrink-0 mt-0.5"></i>
                <span className="text-base">中山間地や変形地・細かな区画の田んぼや畑をお持ちの方</span>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-check-double-line text-xl flex-shrink-0 mt-0.5"></i>
                <span className="text-base">ブームスプレーヤーなど大型機械が入りづらい条件不利地でお困りの方</span>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-check-double-line text-xl flex-shrink-0 mt-0.5"></i>
                <span className="text-base">10反〜数十反規模の水稲・畑作を抱える農業法人・集落営農組織</span>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-check-double-line text-xl flex-shrink-0 mt-0.5"></i>
                <span className="text-base">水稲と合わせて、野菜や果樹も栽培していて、防除作業が煩雑になっている方</span>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-check-double-line text-xl flex-shrink-0 mt-0.5"></i>
                <span className="text-base">ドローンの導入を一度は検討したが、免許取得・機器購入・メンテナンスなどの運用に不安を感じた方</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ご利用の流れ Section */}
      <section id="flow" className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              ご依頼から散布までの流れ
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              初めての方でも、難しい手続きはありません。<br />
              農薬取締法に基づき登録された農薬を用い、ラベルに従った適正使用で散布します。
            </p>
          </div>

          <div className="relative">
            <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-teal-200 transform -translate-x-1/2"></div>

            <div className="space-y-12">
              <div className="relative flex items-center md:justify-start">
                <div className="md:w-1/2 md:pr-12 md:text-right">
                  <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200 inline-block">
                    <div className="flex md:flex-row-reverse items-start gap-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center text-white font-bold text-xl">1</div>
                      <div className="md:text-right">
                        <h3 className="text-xl font-bold text-gray-900 mb-2">お問い合わせ</h3>
                        <p className="text-lg text-gray-600 leading-relaxed text-left">電話・お問い合わせフォームから、ほ場の場所・面積・作物・大まかな散布希望時期をお知らせください。</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="hidden md:block absolute left-1/2 w-4 h-4 bg-teal-600 rounded-full transform -translate-x-1/2"></div>
              </div>

              <div className="relative flex items-center md:justify-end">
                <div className="md:w-1/2 md:pl-12">
                  <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200 inline-block">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center text-white font-bold text-xl">2</div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">ヒアリング・概算見積もり</h3>
                        <p className="text-lg text-gray-600 leading-relaxed text-left">圃場条件や作付け状況を伺い、概算の費用とスケジュール感をご案内します。3ヶ月前までのご相談で、早割料金の適用が可能です。</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="hidden md:block absolute left-1/2 w-4 h-4 bg-teal-600 rounded-full transform -translate-x-1/2"></div>
              </div>

              <div className="relative flex items-center md:justify-start">
                <div className="md:w-1/2 md:pr-10 md:text-right">
                  <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200 inline-block">
                    <div className="flex md:flex-row-reverse items-start gap-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center text-white font-bold text-xl">3</div>
                      <div className="md:text-right">
                        <h3 className="text-xl font-bold text-gray-900 mb-2">圃場確認・日程確定</h3>
                        <p className="text-lg text-gray-600 leading-relaxed text-left">必要に応じて圃場を確認し、散布ルートと安全確認を行います。天候状況も踏まえながら、最適な散布日程を調整します。</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="hidden md:block absolute left-1/2 w-4 h-4 bg-teal-600 rounded-full transform -translate-x-1/2"></div>
              </div>

              <div className="relative flex items-center md:justify-end">
                <div className="md:w-1/2 md:pl-12">
                  <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200 inline-block">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center text-white font-bold text-xl">4</div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">散布作業</h3>
                        <p className="text-lg text-gray-600 leading-relaxed text-left">有資格オペレーターが、登録農薬を用いてドローン散布を行います。作業中、お客様は圃場の外で見守っていただくだけで構いません。</p>
                        <p className="text-sm text-gray-500 leading-relaxed mt-3 text-left">※基本的に、当日立ち会いいただきながら作業させていただきます。ご都合により、立ち会いいただくこと無く、作業することも可能です</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="hidden md:block absolute left-1/2 w-4 h-4 bg-teal-600 rounded-full transform -translate-x-1/2"></div>
              </div>

              <div className="relative flex items-center md:justify-start">
                <div className="md:w-1/2 md:pr-12 md:text-right">
                  <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200 inline-block">
                    <div className="flex md:flex-row-reverse items-start gap-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center text-white font-bold text-xl">5</div>
                      <div className="md:text-right">
                        <h3 className="text-xl font-bold text-gray-900 mb-2">作業完了・ご報告</h3>
                        <p className="text-lg text-gray-600 leading-relaxed text-left">散布完了後、散布面積や使用薬剤などの情報をお伝えします。継続的な防除計画のご相談も承ります。</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="hidden md:block absolute left-1/2 w-4 h-4 bg-teal-600 rounded-full transform -translate-x-1/2"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              よくあるご質問
            </h2>
          </div>

          <div className="space-y-6">
            <details className="bg-gray-50 p-6 rounded-2xl group">
              <summary className="font-bold text-xl text-gray-900 cursor-pointer list-none flex items-center justify-between">
                <span>何反からお願いできますか？</span>
                <i className="ri-arrow-down-s-line text-2xl text-teal-600 group-open:rotate-180 transition-transform"></i>
              </summary>
              <p className="mt-4 text-lg text-gray-600 leading-relaxed">小規模でも相談OK。ただし、エリアや時期によっては複数ほ場をまとめてのご対応をお願いする場合があります。まずはお気軽にご相談ください。</p>
            </details>

            <details className="bg-gray-50 p-6 rounded-2xl group">
              <summary className="font-bold text-xl text-gray-900 cursor-pointer list-none flex items-center justify-between">
                <span>農薬はそちらで用意してもらえますか？</span>
                <i className="ri-arrow-down-s-line text-2xl text-teal-600 group-open:rotate-180 transition-transform"></i>
              </summary>
              <p className="mt-4 text-lg text-gray-600 leading-relaxed">基本は弊社指定の登録農薬をこちらで用意して散布します。ご希望の薬剤がある場合はご相談ください。農薬の用法、利用量は農薬容器のラベルに従った分量、包容で散布いたしますのでご安心ください。</p>
            </details>

            <details className="bg-gray-50 p-6 rounded-2xl group">
              <summary className="font-bold text-xl text-gray-900 cursor-pointer list-none flex items-center justify-between">
                <span>雨や風が強い日はどうなりますか？</span>
                <i className="ri-arrow-down-s-line text-2xl text-teal-600 group-open:rotate-180 transition-transform"></i>
              </summary>
              <p className="mt-4 text-lg text-gray-600 leading-relaxed">風が強い日や雨天時は、薬剤の飛散や効果低下を避けるため、日程を変更させていただく場合があります。安全かつ効果的な散布のため、ご理解をお願いいたします。</p>
            </details>

            <details className="bg-gray-50 p-6 rounded-2xl group">
              <summary className="font-bold text-xl text-gray-900 cursor-pointer list-none flex items-center justify-between">
                <span>自宅に誰もいなくても散布してもらえますか？</span>
                <i className="ri-arrow-down-s-line text-2xl text-teal-600 group-open:rotate-180 transition-transform"></i>
              </summary>
              <p className="mt-4 text-lg text-gray-600 leading-relaxed">原則として、初回は立ち会いをお願いしています。2回目以降や条件が整った場合は、事前の打ち合わせ内容に基づき無人散布にも対応可能です。</p>
            </details>

            <details className="bg-gray-50 p-6 rounded-2xl group">
              <summary className="font-bold text-xl text-gray-900 cursor-pointer list-none flex items-center justify-between">
                <span>中山間地や変形地でも本当に大丈夫ですか？</span>
                <i className="ri-arrow-down-s-line text-2xl text-teal-600 group-open:rotate-180 transition-transform"></i>
              </summary>
              <p className="mt-4 text-lg text-gray-600 leading-relaxed">機体や圃場条件にもよりますが、多くの場合、ブームスプレーヤーが入れないような圃場でも散布可能です。事前の圃場確認で、安全に飛行できるかどうかをチェックしたうえでお受けします。</p>
            </details>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact-form" className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              今年の防除計画に、ドローンという選択肢を
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed mb-6 text-left">
              「自分の体力で、いつまでこの散布を続けられるだろう」。<br />
              そんな不安を感じ始めたら、それはドローン散布を検討するタイミングかもしれません。
            </p>
            <p className="text-lg text-gray-600 max-w-4xl mx-auto leading-relaxed text-left">
              茨城県内で一夏につき300万円以上の散布実績があり、すでに多くの農家さんから<br />
              「もっと早く頼めばよかった」という声をいただいています。<br />
              まずは、あなたのほ場の場所・面積・作物を教えてください。最適な散布時期と、1反単価を含めたプランをご提案します。
            </p>
          </div>

          <div className="bg-white p-8 sm:p-12 rounded-3xl shadow-xl">
            <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">無料見積もり・相談フォーム</h3>
            
            <form id="contact-form-element" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-base font-bold text-gray-900 mb-2">お名前 <span className="text-red-500">*</span></label>
                <input 
                  type="text" 
                  id="name" 
                  name="name" 
                  required 
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-base"
                  placeholder="山田太郎"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="phone" className="block text-base font-bold text-gray-900 mb-2">電話番号 <span className="text-red-500">*</span></label>
                  <input 
                    type="tel" 
                    id="phone" 
                    name="phone" 
                    required 
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-base"
                    placeholder="090-1234-5678"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-base font-bold text-gray-900 mb-2">メールアドレス <span className="text-red-500">*</span></label>
                  <input 
                    type="email" 
                    id="email" 
                    name="email" 
                    required 
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-base"
                    placeholder="example@email.com"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="area" className="block text-base font-bold text-gray-900 mb-2">ほ場の場所（市町村） <span className="text-red-500">*</span></label>
                <input 
                  type="text" 
                  id="area" 
                  name="area" 
                  required 
                  value={formData.area}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-base"
                  placeholder="例：鉾田市"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="acreage" className="block text-base font-bold text-gray-900 mb-2">面積（反数） <span className="text-red-500">*</span></label>
                  <input 
                    type="text" 
                    id="acreage" 
                    name="acreage" 
                    required 
                    value={formData.acreage}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-base"
                    placeholder="例：10反"
                  />
                </div>

                <div>
                  <label htmlFor="crop" className="block text-base font-bold text-gray-900 mb-2">作物 <span className="text-red-500">*</span></label>
                  <select 
                    id="crop" 
                    name="crop" 
                    required 
                    value={formData.crop}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-base"
                  >
                    <option value="">選択してください</option>
                    <option value="水稲">水稲</option>
                    <option value="芋・馬鈴薯">芋・馬鈴薯</option>
                    <option value="麦・そば">麦・そば</option>
                    <option value="露路野菜">露路野菜</option>
                    <option value="果樹">果樹</option>
                    <option value="その他">その他</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="schedule" className="block text-base font-bold text-gray-900 mb-2">散布希望時期</label>
                <input 
                  type="text" 
                  id="schedule" 
                  name="schedule" 
                  value={formData.schedule}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-base"
                  placeholder="例：6月上旬、7月中旬など"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-base font-bold text-gray-900 mb-2">その他ご質問・ご要望</label>
                <textarea 
                  id="message" 
                  name="message" 
                  rows={5} 
                  maxLength={500}
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-base resize-none"
                  placeholder="ご質問やご要望がありましたらご記入ください（500文字以内）"
                ></textarea>
                <p className="text-sm text-gray-500 mt-1">{formData.message.length}/500文字</p>
              </div>

              {submitStatus === 'success' && (
                <div className="bg-teal-50 border border-teal-200 text-teal-800 px-4 py-3 rounded-lg">
                  <p className="text-base font-medium">送信が完了しました。担当者より折り返しご連絡いたします。</p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
                  <p className="text-base font-medium">送信に失敗しました。お手数ですが、もう一度お試しください。</p>
                </div>
              )}

              <button 
                type="submit" 
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-teal-600 text-white text-xl font-bold rounded-full hover:bg-teal-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isSubmitting ? '送信中...' : '無料見積もりを依頼する'}
              </button>
            </form>

            <div className="mt-8 pt-8 border-t border-gray-200">
              <p className="text-center text-base font-bold text-gray-900 mb-4">お急ぎの方はお電話でも受付中</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <a href="tel:08022504380" className="flex items-center gap-3 px-6 py-3 bg-gray-900 text-white rounded-full hover:bg-gray-800 transition-colors whitespace-nowrap">
                  <i className="ri-phone-line text-xl"></i>
                  <span className="text-lg font-bold">080-2250-4380</span>
                </a>
                <span className="text-base text-gray-600">受付時間：9時〜17時</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 会社概要 Section */}
      <section id="company" className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-12 text-center">会社概要</h2>
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <dl className="divide-y divide-gray-100">
              <div className="px-6 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                <dt className="text-sm font-semibold text-gray-700 sm:col-span-1">会社名</dt>
                <dd className="mt-1 text-base text-gray-900 sm:mt-0 sm:col-span-2">株式会社つくし映像</dd>
              </div>
              <div className="px-6 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                <dt className="text-sm font-semibold text-gray-700 sm:col-span-1">設立</dt>
                <dd className="mt-1 text-base text-gray-900 sm:mt-0 sm:col-span-2">2021年7月</dd>
              </div>
              <div className="px-6 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                <dt className="text-sm font-semibold text-gray-700 sm:col-span-1">所在地</dt>
                <dd className="mt-1 text-base text-gray-900 sm:mt-0 sm:col-span-2">〒311-2103 茨城県鉾田市秋山618番地2</dd>
              </div>
              <div className="px-6 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                <dt className="text-sm font-semibold text-gray-700 sm:col-span-1">事業内容</dt>
                <dd className="mt-1 text-base text-gray-900 sm:mt-0 sm:col-span-2">
                  映像制作・ドローン農薬散布代行。茨城県全域を対象に、稲作・野菜・果樹の防除作業をドローンで代行し、農家の省力化と安全な防除をサポートしています。
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-teal-700 to-teal-800 text-white py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <img src="https://static.readdy.ai/image/128f281c68d0bcc2a1e3f255cb4aab26/044fcb80aa421476fb1a240214d755ab.jpeg" alt="ドローン農薬散布サービス" className="h-12 w-auto mb-4" />
              <p className="text-base text-teal-100 leading-relaxed">茨城県全域対応のドローン農薬散布代行サービス。重労働の防除作業を、安全・確実にサポートします。</p>
            </div>

            <div>
              <h4 className="text-xl font-bold mb-4">サービス</h4>
              <ul className="space-y-2 text-base text-teal-100">
                <li><a href="#merit" className="hover:text-white transition-colors">サービスの特徴</a></li>
                <li><a href="#price" className="hover:text-white transition-colors">料金プラン</a></li>
                <li><a href="#area" className="hover:text-white transition-colors">対応エリア</a></li>
                <li><a href="#flow" className="hover:text-white transition-colors">ご利用の流れ</a></li>
                <li><a href="#company" className="hover:text-white transition-colors">会社概要</a></li>
                <li><Link to="/commercial-transaction" className="hover:text-white transition-colors">特定商取引法に関する表記</Link></li>
                <li><Link to="/privacy-policy" className="hover:text-white transition-colors">プライバシーポリシー</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-xl font-bold mb-4">お問い合わせ</h4>
              <ul className="space-y-3 text-base text-teal-100">
                <li className="flex items-center gap-2">
                  <i className="ri-phone-line"></i>
                  <a href="tel:08022504380" className="hover:text-white transition-colors">080-2250-4380</a>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-time-line"></i>
                  <span>受付時間：9時〜17時</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-map-pin-line"></i>
                  <span>対応エリア：茨城県全域</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-teal-600 pt-8 text-center text-base text-teal-100">
            <p>&copy; 2026 株式会社つくし映像　ドローン農薬散布サービス. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
